import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Building2,
  Calculator,
  ContactRound,
  FileText,
  KeyRound,
  Landmark,
  Pencil,
  ShieldCheck,
  UserRoundCheck,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Breadcrumbs,
  EmptyState,
  Loading,
  SocietyPageHeader,
  WorkflowGuide,
  useTabParam,
} from "../../components/shared";
import { useAuth } from "../../context/AuthContext";
import { useSocietyFeatures } from "../../context/SocietyFeatureContext";
import CommitteeMembersPanel from "./CommitteeMembersPanel";
import SocietyAccessPanel from "./SocietyAccessPanel";

const CATEGORIES = [
  "Registration Certificate",
  "PAN",
  "Bye-laws",
  "Conveyance",
  "Property Documents",
  "Insurance",
  "Committee Documents",
  "Bank Documents",
  "Contracts",
  "Government / Statutory",
  "Other",
];

export default function SocietySetup() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [context, setContext] = useState(null);
  const [profile, setProfile] = useState(null);
  const [structure, setStructure] = useState(null);
  const [units, setUnits] = useState(null);
  const [governance, setGovernance] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [banks, setBanks] = useState(null);
  const [roles, setRoles] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const logoRef = useRef();
  const docRef = useRef();

  const permissions = context?.permissions || {};
  const can = (module, action = "view") => Boolean(permissions[module]?.includes(action));

  const load = async () => {
    try {
      const [dash, ctx, prof] = await Promise.all([
        api.get("/society-setup/dashboard"),
        api.get("/society-setup/context"),
        api.get("/society-setup/profile"),
      ]);
      setDashboard(dash.data);
      setContext(ctx.data);
      setProfile(prof.data);
      const tasks = [];
      tasks.push(
        ctx.data.permissions?.structure?.includes("view")
          ? api.get("/society-setup/structure")
          : Promise.resolve({ data: null }),
      );
      tasks.push(
        ctx.data.permissions?.units?.includes("view")
          ? api.get("/society-setup/units")
          : Promise.resolve({ data: null }),
      );
      tasks.push(
        ctx.data.permissions?.governance?.includes("view")
          ? api.get("/society-setup/governance")
          : Promise.resolve({ data: null }),
      );
      tasks.push(
        ctx.data.permissions?.documents?.includes("view")
          ? api.get("/society-setup/documents")
          : Promise.resolve({ data: null }),
      );
      tasks.push(
        ctx.data.permissions?.settings?.includes("view")
          ? api.get("/society-setup/bank-accounts")
          : Promise.resolve({ data: null }),
      );
      const [structureData, unitData, governanceData, documentData, bankData] =
        await Promise.all(tasks);
      setStructure(structureData.data);
      setUnits(unitData.data);
      setGovernance(governanceData.data);
      setDocuments(documentData.data);
      setBanks(bankData.data);
      if (ctx.data.permissions?.settings?.includes("manage")) {
        const [roleData, membershipData] = await Promise.all([
          api.get("/society-setup/roles"),
          api.get("/society-setup/memberships"),
        ]);
        setRoles(roleData.data);
        setMemberships(membershipData.data);
      } else {
        setRoles([]);
        setMemberships([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load Society Setup");
      setDashboard({});
    }
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    try {
      const response = await api.put("/society-setup/profile", profile);
      setProfile(response.data);
      toast.success("Society profile updated");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update profile");
    }
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/society-setup/profile/logo", form);
      toast.success("Society logo updated");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not upload logo");
    }
    event.target.value = "";
  };

  const tabOptions = useMemo(
    () => [
      ["profile", "Profile", "profile", "view"],
      ["structure", "Structure", "structure", "view"],
      ["governance", "Governance", "governance", "view"],
      ["documents", "Documents", "documents", "view"],
      ["settings", "Financial foundation", "settings", "view"],
      ["access", "Society access", "settings", "manage"],
    ].filter(([, , module, action]) => Boolean(permissions[module]?.includes(action))),
    [permissions],
  );
  const { byKey } = useSocietyFeatures();
  const featureOn = (key) => Boolean(byKey?.(key)?.allowed);
  const tabValues = tabOptions.map(([value]) => value);
  const firstTab = tabValues[0] || "profile";
  const [tabParam, setSetupTab] = useTabParam(firstTab);
  const setupTab = tabValues.includes(tabParam) ? tabParam : firstTab;
  const [searchParams] = useSearchParams();
  const focus = searchParams.get("focus") === "1";
  const focusMeta =
    focus && setupTab === "governance"
      ? { title: "Committee & Roles", subtitle: "Define committee designations, create society users, and assign their roles.", icon: UsersRound }
      : focus && setupTab === "access"
        ? { title: "Access & Permissions", subtitle: "Manage society user accounts and the roles that control their permissions.", icon: KeyRound }
        : { title: "Society Setup & Governance", subtitle: "A tenant-secure foundation for your society administration.", icon: ShieldCheck };

  if (!dashboard || !profile) return <Loading />;

  return (
    <div data-testid="society-setup-page">
      <Breadcrumbs
        items={[
          { label: "Dashboard", to: "/app" },
          { label: "Society & Members" },
          { label: "Society Setup" },
        ]}
      />
      <SocietyPageHeader
        icon={focusMeta.icon}
        tone="blue"
        title={focusMeta.title}
        subtitle={focusMeta.subtitle}
      />
      {!focus && (
      <section
        className="border-l-4 border-primary bg-primary/5 p-5"
        data-testid="society-setup-checklist"
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Setup completion</div>
            <div className="font-display text-4xl font-bold">
              {dashboard.completion_percent || 0}%
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-2 text-sm sm:grid-cols-4">
            {Object.entries(dashboard.checklist || {}).map(([key, done]) => (
              <div data-testid={`society-check-${key}`} key={key}>
                <span className={done ? "text-emerald-700" : "text-muted-foreground"}>
                  {done ? "●" : "○"}
                </span>{" "}
                {key.replaceAll("_", " ")}
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <Tabs className="mt-7" onValueChange={setSetupTab} value={setupTab}>
        {!focus && (
        <TabsList className="h-auto flex-wrap">
          {tabOptions.map(([value, label]) => (
            <TabsTrigger
              data-testid={`society-setup-tab-${value}`}
              key={value}
              value={value}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        )}
        {can("profile") && (
          <TabsContent value="profile">
            <ProfilePanel
              canEdit={can("profile", "edit")}
              logoRef={logoRef}
              onLogo={uploadLogo}
              onSave={saveProfile}
              profile={profile}
              setProfile={setProfile}
            />
          </TabsContent>
        )}
        {can("structure") && (
          <TabsContent value="structure">
            <StructurePanel
              canEdit={can("structure", "create")}
              canEditUnits={can("units", "edit")}
              canManageUnitMembers={
                user?.role !== "society_portal" &&
                can("member_master", "view") &&
                can("units", "view")
              }
              onSaved={load}
              structure={structure}
              units={units}
            />
          </TabsContent>
        )}
        {can("governance") && (
          <TabsContent value="governance">
            <GovernancePanel
              canEdit={can("governance", "create")}
              governance={governance}
              onSaved={load}
            />
            <CommitteeMembersPanel
              canEdit={can("governance", "edit")}
              governance={governance}
              onSaved={load}
            />
            {focus && can("settings", "manage") && (
              <div className="mt-6" data-testid="governance-access-panel">
                <SocietyAccessPanel
                  memberships={memberships}
                  onSaved={load}
                  roles={roles}
                />
              </div>
            )}
          </TabsContent>
        )}
        {can("documents") && (
          <TabsContent value="documents">
            <DocumentsPanel
              canEdit={can("documents", "create")}
              docRef={docRef}
              documents={documents}
              onSaved={load}
            />
          </TabsContent>
        )}
        {can("settings") && (
          <TabsContent value="settings">
            <FinancialPanel
              banks={banks}
              canEdit={can("settings", "edit")}
              onSaved={load}
              profile={profile}
              setProfile={setProfile}
            />
          </TabsContent>
        )}
        {can("settings", "manage") && (
          <TabsContent value="access">
            <div data-testid={focus ? "governance-access-panel" : undefined}>
              <SocietyAccessPanel
                memberships={memberships}
                onSaved={load}
                roles={roles}
              />
            </div>
          </TabsContent>
        )}
      </Tabs>

      {!focus && (
      <WorkflowGuide
        steps={[
          {
            label: "Members & Units",
            to: "/app/directory",
            icon: ContactRound,
            testid: "setup-guide-directory",
            show: can("member_master", "view") && featureOn("sm02_directory"),
          },
          {
            label: "Maintenance Finance",
            to: "/app/finance",
            icon: Landmark,
            testid: "setup-guide-finance",
            show: can("finance_billing", "view") && featureOn("sm03_billing"),
          },
          {
            label: "Society Accounting",
            to: "/app/accounting",
            icon: Calculator,
            testid: "setup-guide-accounting",
            show: can("accounting_settings", "view") && featureOn("sm04_accounting"),
          },
        ]}
        title="Continue the Unit-to-Ledger workflow"
      />
      )}
    </div>
  );
}

function ProfilePanel({ canEdit, logoRef, onLogo, onSave, profile, setProfile }) {
  const set = (key, value) => setProfile({ ...profile, [key]: value });

  return (
    <section
      className="mt-5 grid gap-6 lg:grid-cols-[1fr_280px]"
      data-testid="society-profile-panel"
    >
      <div className="grid gap-4 border bg-card p-5 sm:grid-cols-2">
        <Field label="Society name">
          <Input
            data-testid="society-profile-name"
            disabled={!canEdit}
            onChange={(event) => set("name", event.target.value)}
            value={profile.name || ""}
          />
        </Field>
        <Field label="Regional / local name (optional)">
          <Input
            data-testid="society-profile-regional-name"
            disabled={!canEdit}
            onChange={(event) => set("regional_name", event.target.value)}
            value={profile.regional_name || ""}
          />
        </Field>
        <Field label="Registration number">
          <Input
            data-testid="society-profile-registration-number"
            disabled={!canEdit}
            onChange={(event) => set("registration_number", event.target.value)}
            value={profile.registration_number || ""}
          />
        </Field>
        <Field label="Registration date">
          <Input
            data-testid="society-profile-registration-date"
            disabled={!canEdit}
            onChange={(event) => set("registration_date", event.target.value)}
            type="date"
            value={profile.registration_date || ""}
          />
        </Field>
        <Field label="Society type">
          <select
            data-testid="society-profile-type"
            disabled={!canEdit}
            onChange={(event) => set("society_type", event.target.value)}
            value={profile.society_type || ""}
          >
            <option value="">Select type</option>
            <option>Co-operative Housing Society</option>
            <option>Apartment Association</option>
            <option>Condominium</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Official email">
          <Input
            data-testid="society-profile-email"
            disabled={!canEdit}
            onChange={(event) => set("official_email", event.target.value)}
            value={profile.official_email || ""}
          />
        </Field>
        <Field label="Address" wide>
          <Input
            data-testid="society-profile-address"
            disabled={!canEdit}
            onChange={(event) => set("address", event.target.value)}
            value={profile.address || ""}
          />
        </Field>
        <Field label="City">
          <Input
            data-testid="society-profile-city"
            disabled={!canEdit}
            onChange={(event) => set("city", event.target.value)}
            value={profile.city || ""}
          />
        </Field>
        <Field label="State">
          <Input
            data-testid="society-profile-state"
            disabled={!canEdit}
            onChange={(event) => set("state", event.target.value)}
            value={profile.state || ""}
          />
        </Field>
        <Field label="PIN code">
          <Input
            data-testid="society-profile-pincode"
            disabled={!canEdit}
            onChange={(event) => set("pincode", event.target.value)}
            value={profile.pincode || ""}
          />
        </Field>
        <Field label="Primary contact">
          <Input
            data-testid="society-profile-contact"
            disabled={!canEdit}
            onChange={(event) => set("primary_contact", event.target.value)}
            value={profile.primary_contact || ""}
          />
        </Field>
        <Field label="PAN">
          <Input
            data-testid="society-profile-pan"
            disabled={!canEdit}
            onChange={(event) => set("pan", event.target.value)}
            value={profile.pan || ""}
          />
        </Field>
        <Field label="GSTIN">
          <Input
            data-testid="society-profile-gstin"
            disabled={!canEdit}
            onChange={(event) => set("gstin", event.target.value)}
            value={profile.gstin || ""}
          />
        </Field>
        {canEdit && (
          <Button data-testid="society-profile-save" onClick={onSave}>
            Save profile
          </Button>
        )}
      </div>
      <aside className="border bg-card p-5">
        <h2 className="font-display font-bold">Society branding</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your uploaded Society logo is reused on Letterhead and official printable documents.
        </p>
        <div className="mt-5 flex aspect-square items-center justify-center border bg-muted">
          {profile.has_logo ? (
            <img
              alt="Society logo"
              className="h-full w-full object-contain"
              data-testid="society-profile-logo"
              src={`${API}/society-setup/profile/logo`}
            />
          ) : (
            <span className="text-center text-sm text-muted-foreground" data-testid="society-profile-no-logo">
              No Society logo uploaded
            </span>
          )}
        </div>
        {canEdit && (
          <>
            <input
              accept="image/*"
              data-testid="society-logo-input"
              hidden
              onChange={onLogo}
              ref={logoRef}
              type="file"
            />
            <Button
              className="mt-4 w-full"
              data-testid="society-logo-upload"
              onClick={() => logoRef.current?.click()}
            >
              Upload logo
            </Button>
          </>
        )}
        <a className="mt-3 block" data-testid="society-profile-letterhead-link" href="/app/letterhead">
          <Button className="w-full" variant="outline">
            Preview Society Letterhead
          </Button>
        </a>
      </aside>
    </section>
  );
}

function StructurePanel({
  canEdit,
  canEditUnits,
  canManageUnitMembers,
  onSaved,
  structure,
  units,
}) {
  const [building, setBuilding] = useState({
    building_code: "",
    floors_description: "",
    has_stilt: false,
    name: "",
  });
  const [wing, setWing] = useState({ name: "", building_id: "", floors: "" });
  const [unit, setUnit] = useState({
    built_up_area: "",
    carpet_area: "",
    custom_chargeable_area: "",
    floor: "",
    joint_owner_person_ids: [],
    primary_owner_person_id: "",
    super_built_up_area: "",
    building_id: "",
    unit_number: "",
    unit_type: "FLAT",
  });
  const [people, setPeople] = useState([]);
  const [quickMember, setQuickMember] = useState(null);
  const [bulk, setBulk] = useState({
    built_up_area: "",
    building_id: "",
    carpet_area: "",
    custom_chargeable_area: "",
    floor: "",
    unit_numbers: "",
    unit_type: "FLAT",
    super_built_up_area: "",
  });
  const [bulkPreview, setBulkPreview] = useState(null);
  const [editUnit, setEditUnit] = useState(null);
  const [editUnitForm, setEditUnitForm] = useState(null);
  const [unitImportJob, setUnitImportJob] = useState(null);
  const [unitImportMode, setUnitImportMode] = useState("UPSERT");

  const validateUnitImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const response = await api.post("/society-setup/units/import/validate", form);
      setUnitImportJob(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not validate the Units file");
    }
    event.target.value = "";
  };
  const confirmUnitImport = async () => {
    try {
      const response = await api.post(
        `/society-setup/units/import/${unitImportJob.unit_import_job_id}/confirm`,
        { mode: unitImportMode },
      );
      toast.success(`${response.data.created} created · ${response.data.updated} updated · ${response.data.skipped} skipped`);
      setUnitImportJob(null);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not confirm the Units import");
    }
  };

  const loadPeople = async () => {
    try {
      const response = await api.get("/society-directory/people", {
        params: { limit: 500 },
      });
      setPeople(response.data.items || []);
    } catch {
      setPeople([]);
    }
  };

  useEffect(() => {
    loadPeople();
  }, []);

  const post = async (path, payload, reset) => {
    try {
      await api.post(path, payload);
      toast.success("Saved");
      reset();
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save");
    }
  };

  const saveUnit = async () => {
    try {
      await api.post("/society-setup/units", unit);
      toast.success("Unit and owner relationships saved");
      setUnit({
        built_up_area: "",
        carpet_area: "",
        custom_chargeable_area: "",
        floor: "",
        joint_owner_person_ids: [],
        primary_owner_person_id: "",
        super_built_up_area: "",
        building_id: "",
        unit_number: "",
        unit_type: "FLAT",
      });
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save unit");
    }
  };

  const addMember = async () => {
    try {
      const response = await api.post("/society-directory/people", quickMember);
      await loadPeople();
      setUnit({ ...unit, primary_owner_person_id: response.data.person_id });
      setQuickMember(null);
      toast.success("Member added and selected as Primary Owner");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add member");
    }
  };

  const previewBulk = async () => {
    try {
      const response = await api.post("/society-setup/units/bulk/preview", bulk);
      setBulkPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not preview bulk Units");
    }
  };

  const createBulk = async () => {
    try {
      const response = await api.post("/society-setup/units/bulk", bulk);
      toast.success(`${response.data.created} Units created`);
      setBulk({
        built_up_area: "",
        building_id: "",
        carpet_area: "",
        custom_chargeable_area: "",
        floor: "",
        super_built_up_area: "",
        unit_numbers: "",
        unit_type: "FLAT",
      });
      setBulkPreview(null);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create bulk Units");
    }
  };

  const openUnitEditor = (item) => {
    setEditUnit(item);
    setEditUnitForm({
      building_id: item.building_id || "",
      carpet_area: item.carpet_area || "",
      floor: item.floor || "",
      status: item.status || "ACTIVE",
      unit_number: item.unit_number || "",
      unit_type: item.unit_type || "FLAT",
      wing_id: item.wing_id || "",
    });
  };

  const saveUnitEdit = async () => {
    if (!editUnit || !editUnitForm) return;
    try {
      await api.patch(`/society-setup/units/${editUnit.unit_id}`, editUnitForm);
      toast.success("Unit details updated");
      setEditUnit(null);
      setEditUnitForm(null);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update unit details");
    }
  };

  const editWings = (structure?.wings || []).filter((wing) => (
    !editUnitForm?.building_id || wing.building_id === editUnitForm.building_id
  ));

  if (!structure) return <Loading />;

  return (
    <section className="mt-5 space-y-6" data-testid="society-structure-panel">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StructureMetric label="Buildings" value={structure.summary?.buildings || 0} />
        <StructureMetric label="Total Premises" value={structure.summary?.premises || 0} />
        <StructureMetric
          label="Total Chargeable Area"
          value={`${Number(structure.summary?.total_area_sqft || 0).toLocaleString()} Sq.Ft.`}
        />
        <StructureMetric
          label="Parking Areas"
          value={structure.buildings.reduce(
            (count, item) => count + (item.summary?.parking_areas || 0),
            0,
          )}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <SetupCard icon={Building2} title="Building Master">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Building Code *">
              <Input
                data-testid="structure-building-code"
                disabled={!canEdit}
                onChange={(event) => setBuilding({ ...building, building_code: event.target.value })}
                placeholder="A1"
                value={building.building_code}
              />
            </Field>
            <Field label="Building Name">
              <Input
                data-testid="structure-building-name"
                disabled={!canEdit}
                onChange={(event) => setBuilding({ ...building, name: event.target.value })}
                placeholder="A1 Building"
                value={building.name}
              />
            </Field>
            <Field label="Floors / description">
              <Input
                data-testid="structure-building-floors"
                disabled={!canEdit}
                onChange={(event) => setBuilding({ ...building, floors_description: event.target.value })}
                placeholder="Ground + 3"
                value={building.floors_description}
              />
            </Field>
            <label className="flex items-end gap-2 pb-2 text-sm">
              <input
                checked={building.has_stilt}
                data-testid="structure-building-stilt"
                disabled={!canEdit}
                onChange={(event) => setBuilding({ ...building, has_stilt: event.target.checked })}
                type="checkbox"
              />
              Stilt parking available
            </label>
          </div>
          {canEdit && (
            <Button
              className="mt-4"
              data-testid="structure-building-save"
              onClick={() => {
                post("/society-setup/structure/buildings", building, () => {
                  setBuilding({ building_code: "", floors_description: "", has_stilt: false, name: "" });
                });
              }}
              size="sm"
            >
              Save Building
            </Button>
          )}
        </SetupCard>

        <SetupCard icon={Building2} title="Wings">
          <SetupForm
            canEdit={canEdit}
            fields={[["Wing name", "name"], ["Floors", "floors", "number"]]}
            onChange={(key, value) => setWing({ ...wing, [key]: value })}
            onSave={() => {
              post("/society-setup/structure/wings", wing, () => {
                setWing({ name: "", building_id: "", floors: "" });
              });
            }}
            values={wing}
          >
            <select
              data-testid="society-wing-building"
              disabled={!canEdit}
              onChange={(event) => setWing({ ...wing, building_id: event.target.value })}
              value={wing.building_id}
            >
              <option value="">No building / standalone</option>
              {structure.buildings.map((item) => (
                <option key={item.building_id} value={item.building_id}>
                  {item.building_code || item.name}
                </option>
              ))}
            </select>
          </SetupForm>
        </SetupCard>
      </div>

      <SetupCard icon={Landmark} title="Unit Master">
        <p className="mb-4 text-sm text-muted-foreground">
          Carpet Area remains the current Per Sq.Ft. billing input. The other area records are
          reference-only until a future billing policy explicitly changes them.
        </p>
        <div className="mb-5 rounded border bg-muted/20 p-4" data-testid="unit-excel-bar">
          <div className="flex flex-wrap items-center gap-3">
            <a data-testid="unit-template-download" href={`${API}/society-setup/units/template`}>
              <Button size="sm" type="button" variant="outline">Download sample Excel</Button>
            </a>
            <a data-testid="unit-export-download" href={`${API}/society-setup/units/export`}>
              <Button size="sm" type="button" variant="outline">Export existing Units</Button>
            </a>
            {canEditUnits && (
              <label
                className="inline-flex cursor-pointer items-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
                data-testid="unit-import-label"
                htmlFor="unit-import-file"
              >
                Import Units (.xlsx)
                <input accept=".xlsx" className="hidden" data-testid="unit-import-file" id="unit-import-file" onChange={validateUnitImport} type="file" />
              </label>
            )}
          </div>
          {unitImportJob && (
            <div className="mt-4 border bg-card p-4" data-testid="unit-import-preview">
              <p className="text-sm font-semibold" data-testid="unit-import-summary">
                {unitImportJob.summary.total_rows} rows · {unitImportJob.summary.new_rows} new · {unitImportJob.summary.update_rows} update · {unitImportJob.summary.error_rows} errors
              </p>
              {unitImportJob.errors?.length > 0 && (
                <ul className="mt-2 max-h-32 overflow-auto text-xs text-destructive" data-testid="unit-import-errors">
                  {unitImportJob.errors.map((error) => (
                    <li key={error.row}>Row {error.row} ({error.unit_number || "-"}): {error.errors.join(", ")}</li>
                  ))}
                </ul>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <select data-testid="unit-import-mode" onChange={(event) => setUnitImportMode(event.target.value)} value={unitImportMode}>
                  <option value="CREATE">Create new only</option>
                  <option value="UPDATE">Update existing only</option>
                  <option value="UPSERT">Create new and update existing</option>
                </select>
                <Button data-testid="unit-import-confirm" disabled={unitImportJob.summary.valid_rows === 0} onClick={confirmUnitImport} size="sm">
                  Confirm import
                </Button>
                <Button data-testid="unit-import-cancel" onClick={() => setUnitImportJob(null)} size="sm" variant="ghost">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <Field label="Building / Wing *">
            <select
              data-testid="structure-unit-building"
              disabled={!canEdit}
              onChange={(event) => setUnit({ ...unit, building_id: event.target.value })}
              value={unit.building_id}
            >
              <option value="">Choose Building</option>
              {structure.buildings.map((item) => (
                <option key={item.building_id} value={item.building_id}>
                  {item.building_code || item.name} · {item.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Unit Number *">
            <Input
              data-testid="structure-unit-number"
              disabled={!canEdit}
              onChange={(event) => setUnit({ ...unit, unit_number: event.target.value })}
              placeholder="103"
              value={unit.unit_number}
            />
          </Field>
          <Field label="Unit Type *">
            <select
              data-testid="structure-unit-type"
              disabled={!canEdit}
              onChange={(event) => setUnit({ ...unit, unit_type: event.target.value })}
              value={unit.unit_type}
            >
              <option value="FLAT">Flat</option>
              <option value="SHOP">Shop</option>
              <option value="OFFICE">Office</option>
              <option value="COMMERCIAL_UNIT">Commercial Unit</option>
              <option value="GARAGE">Garage</option>
              <option value="GODOWN">Godown</option>
              <option value="OTHER">Other</option>
            </select>
          </Field>
          <Field label="Floor">
            <Input
              data-testid="structure-unit-floor"
              disabled={!canEdit}
              onChange={(event) => setUnit({ ...unit, floor: event.target.value })}
              placeholder="1"
              value={unit.floor}
            />
          </Field>
          <Field label="Carpet Area (Sq.Ft.)">
            <Input
              data-testid="structure-unit-chargeable-area"
              disabled={!canEdit}
              min="0"
              onChange={(event) => setUnit({ ...unit, carpet_area: event.target.value })}
              placeholder="750"
              step="0.01"
              type="number"
              value={unit.carpet_area}
            />
          </Field>
          <Field label="Built-up Area (Sq.Ft.)">
            <Input
              data-testid="structure-unit-built-up-area"
              disabled={!canEdit}
              min="0"
              onChange={(event) => setUnit({ ...unit, built_up_area: event.target.value })}
              step="0.01"
              type="number"
              value={unit.built_up_area}
            />
          </Field>
          <Field label="Super Built-up Area (Sq.Ft.)">
            <Input
              data-testid="structure-unit-super-built-up-area"
              disabled={!canEdit}
              min="0"
              onChange={(event) => setUnit({ ...unit, super_built_up_area: event.target.value })}
              step="0.01"
              type="number"
              value={unit.super_built_up_area}
            />
          </Field>
          <Field label="Custom Chargeable Area (Sq.Ft.)">
            <Input
              data-testid="structure-unit-custom-chargeable-area"
              disabled={!canEdit}
              min="0"
              onChange={(event) => setUnit({ ...unit, custom_chargeable_area: event.target.value })}
              step="0.01"
              type="number"
              value={unit.custom_chargeable_area}
            />
          </Field>
          <Field label="Primary Owner">
            <select
              data-testid="structure-unit-primary-owner"
              disabled={!canEdit}
              onChange={(event) => setUnit({ ...unit, primary_owner_person_id: event.target.value })}
              value={unit.primary_owner_person_id}
            >
              <option value="">Select Member</option>
              {people.map((person) => (
                <option key={person.person_id} value={person.person_id}>
                  {person.member_number ? `${person.member_number} · ` : ""}{person.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Joint Owner(s)">
            <select
              data-testid="structure-unit-joint-owners"
              disabled={!canEdit}
              multiple
              onChange={(event) => {
                const values = [...event.target.selectedOptions].map((option) => option.value);
                setUnit({ ...unit, joint_owner_person_ids: values });
              }}
              value={unit.joint_owner_person_ids}
            >
              {people
                .filter((person) => person.person_id !== unit.primary_owner_person_id)
                .map((person) => (
                  <option key={person.person_id} value={person.person_id}>
                    {person.member_number ? `${person.member_number} · ` : ""}{person.display_name}
                  </option>
                ))}
            </select>
          </Field>
        </div>
        {canEdit && (
          <div className="mt-4 flex flex-wrap gap-3">
            <Button data-testid="structure-unit-save" onClick={saveUnit} size="sm">
              Save Unit
            </Button>
            <Button
              data-testid="structure-unit-quick-add-member"
              onClick={() => {
                setQuickMember({
                  date_of_birth: "",
                  email: "",
                  first_name: "",
                  mobile: "",
                  occupation: "",
                });
              }}
              size="sm"
              variant="outline"
            >
              Add New Member
            </Button>
          </div>
        )}
        {quickMember && (
          <div className="mt-5 grid gap-3 border bg-muted/20 p-4 md:grid-cols-4" data-testid="structure-quick-member-form">
            <Input
              data-testid="structure-quick-member-name"
              onChange={(event) => setQuickMember({ ...quickMember, first_name: event.target.value })}
              placeholder="Full name *"
              value={quickMember.first_name}
            />
            <Input
              data-testid="structure-quick-member-mobile"
              onChange={(event) => setQuickMember({ ...quickMember, mobile: event.target.value })}
              placeholder="Mobile"
              value={quickMember.mobile}
            />
            <Input
              data-testid="structure-quick-member-email"
              onChange={(event) => setQuickMember({ ...quickMember, email: event.target.value })}
              placeholder="Email"
              value={quickMember.email}
            />
            <Input
              data-testid="structure-quick-member-dob"
              onChange={(event) => {
                setQuickMember({ ...quickMember, date_of_birth: event.target.value });
              }}
              type="date"
              value={quickMember.date_of_birth}
            />
            <Input
              data-testid="structure-quick-member-profession"
              onChange={(event) => {
                setQuickMember({ ...quickMember, occupation: event.target.value });
              }}
              placeholder="Profession"
              value={quickMember.occupation}
            />
            <Button data-testid="structure-quick-member-save" onClick={addMember}>
              Save & Select
            </Button>
          </div>
        )}
        <section className="mt-6 border-t pt-5" data-testid="structure-bulk-units">
          <h3 className="font-display text-lg font-bold">Bulk Create Units</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter one Unit Number per line. Review the preview before creating records.
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <select
              data-testid="structure-bulk-building"
              disabled={!canEdit}
              onChange={(event) => setBulk({ ...bulk, building_id: event.target.value })}
              value={bulk.building_id}
            >
              <option value="">Choose Building</option>
              {structure.buildings.map((item) => (
                <option key={item.building_id} value={item.building_id}>
                  {item.building_code || item.name}
                </option>
              ))}
            </select>
            <select
              data-testid="structure-bulk-unit-type"
              disabled={!canEdit}
              onChange={(event) => setBulk({ ...bulk, unit_type: event.target.value })}
              value={bulk.unit_type}
            >
              <option value="FLAT">Flat</option>
              <option value="SHOP">Shop</option>
              <option value="OFFICE">Office</option>
              <option value="COMMERCIAL_UNIT">Commercial Unit</option>
              <option value="GARAGE">Garage</option>
              <option value="GODOWN">Godown</option>
              <option value="OTHER">Other</option>
            </select>
            <Input
              data-testid="structure-bulk-floor"
              disabled={!canEdit}
              onChange={(event) => setBulk({ ...bulk, floor: event.target.value })}
              placeholder="Floor"
              value={bulk.floor}
            />
            <Input
              data-testid="structure-bulk-area"
              disabled={!canEdit}
              min="0"
              onChange={(event) => setBulk({ ...bulk, carpet_area: event.target.value })}
              placeholder="Chargeable Area Sq.Ft."
              step="0.01"
              type="number"
              value={bulk.carpet_area}
            />
            <Input
              data-testid="structure-bulk-built-up-area"
              disabled={!canEdit}
              min="0"
              onChange={(event) => setBulk({ ...bulk, built_up_area: event.target.value })}
              placeholder="Built-up Area Sq.Ft."
              step="0.01"
              type="number"
              value={bulk.built_up_area}
            />
            <Input
              data-testid="structure-bulk-super-built-up-area"
              disabled={!canEdit}
              min="0"
              onChange={(event) => setBulk({ ...bulk, super_built_up_area: event.target.value })}
              placeholder="Super Built-up Area Sq.Ft."
              step="0.01"
              type="number"
              value={bulk.super_built_up_area}
            />
            <Input
              data-testid="structure-bulk-custom-chargeable-area"
              disabled={!canEdit}
              min="0"
              onChange={(event) => setBulk({ ...bulk, custom_chargeable_area: event.target.value })}
              placeholder="Custom Chargeable Area Sq.Ft."
              step="0.01"
              type="number"
              value={bulk.custom_chargeable_area}
            />
          </div>
          <textarea
            className="mt-3 min-h-28 w-full border bg-background p-3 text-sm"
            data-testid="structure-bulk-unit-numbers"
            disabled={!canEdit}
            onChange={(event) => setBulk({ ...bulk, unit_numbers: event.target.value })}
            placeholder={"101\n102\n103"}
            value={bulk.unit_numbers}
          />
          {canEdit && (
            <Button data-testid="structure-bulk-preview" onClick={previewBulk} size="sm" variant="outline">
              Preview Units
            </Button>
          )}
          {bulkPreview && (
            <div className="mt-4 border bg-muted/20 p-4" data-testid="structure-bulk-preview-results">
              <p className="font-medium">{bulkPreview.items.length} Units ready to create</p>
              {bulkPreview.errors.length > 0 && (
                <p className="mt-2 text-sm text-amber-800">
                  {bulkPreview.errors.map((error) => error.message).join(" · ")}
                </p>
              )}
              {bulkPreview.errors.length === 0 && canEdit && (
                <Button className="mt-3" data-testid="structure-bulk-confirm" onClick={createBulk} size="sm">
                  Confirm Bulk Create
                </Button>
              )}
            </div>
          )}
        </section>
      </SetupCard>

      <section className="grid gap-4 xl:grid-cols-2" data-testid="structure-building-detail-list">
        {structure.buildings.map((item) => (
          <article className="border bg-card p-5" data-testid={`structure-building-${item.building_id}`} key={item.building_id}>
            <h2 className="font-display text-xl font-bold">{item.building_code || item.name}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {item.floors_description || item.floors || "Floor details not entered"}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <span>{item.summary?.premises || 0} premises</span>
              <span>{Number(item.summary?.total_area_sqft || 0).toLocaleString()} Sq.Ft.</span>
              {Object.entries(item.summary?.unit_types || {}).map(([type, count]) => (
                <span key={type}>{count} {type.replaceAll("_", " ")}</span>
              ))}
              <span>{item.summary?.parking_areas || 0} parking areas</span>
            </div>
          </article>
        ))}
      </section>

      <SimpleTable
        headers={[
          "Unit",
          "Type",
          "Floor",
          "Carpet Area Sq.Ft.",
          "Primary Owner",
          "Joint Owner(s)",
          "Member No.",
          "Occupancy",
          "Status",
          "Actions",
        ]}
        rows={(units || []).map((item) => [
          item.display_unit_code || item.unit_number,
          item.unit_type,
          item.floor || "—",
          item.carpet_area || "—",
          item.primary_owner_name || "—",
          item.joint_owner_names?.join(", ") || "—",
          item.primary_owner_member_number || "—",
          item.current_occupancy_status || item.occupancy_status || "—",
          item.status,
          (canEditUnits || canManageUnitMembers) ? (
            <div className="flex items-center gap-1">
              {canEditUnits && (
                <Button
                  data-testid={`structure-unit-edit-${item.unit_id}`}
                  onClick={() => openUnitEditor(item)}
                  size="icon"
                  title={`Edit ${item.display_unit_code || item.unit_number}`}
                  variant="ghost"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {canManageUnitMembers && (
                <Button asChild size="icon" variant="ghost">
                  <Link
                    data-testid={`structure-unit-manage-members-${item.unit_id}`}
                    title={`Manage members for ${item.display_unit_code || item.unit_number}`}
                    to={`/app/directory?tab=units&unit=${item.unit_id}`}
                  >
                    <UserRoundCheck className="h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          ) : "—",
        ])}
        testId="society-units-table"
      />
      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            setEditUnit(null);
            setEditUnitForm(null);
          }
        }}
        open={Boolean(editUnit && editUnitForm)}
      >
        <DialogContent data-testid="structure-unit-edit-dialog" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Unit Details</DialogTitle>
            <DialogDescription>
              Ownership, joint owners, tenants, and occupancy are managed in Member Records.
              {editUnit?.identity_edit_locked && (
                <span className="mt-1 block">
                  Unit number and placement are locked because this Unit has member or
                  finance history.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {editUnitForm && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Building">
                <select
                  data-testid="structure-unit-edit-building"
                  disabled={editUnit.identity_edit_locked}
                  onChange={(event) => setEditUnitForm({
                    ...editUnitForm,
                    building_id: event.target.value,
                    wing_id: "",
                  })}
                  value={editUnitForm.building_id}
                >
                  <option value="">No building / standalone</option>
                  {structure.buildings.map((item) => (
                    <option key={item.building_id} value={item.building_id}>
                      {item.building_code || item.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Wing">
                <select
                  data-testid="structure-unit-edit-wing"
                  disabled={editUnit.identity_edit_locked}
                  onChange={(event) => setEditUnitForm({
                    ...editUnitForm,
                    wing_id: event.target.value,
                  })}
                  value={editUnitForm.wing_id}
                >
                  <option value="">No wing</option>
                  {editWings.map((item) => (
                    <option key={item.wing_id} value={item.wing_id}>{item.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Unit Number">
                <Input
                  data-testid="structure-unit-edit-number"
                  disabled={editUnit.identity_edit_locked}
                  onChange={(event) => setEditUnitForm({
                    ...editUnitForm,
                    unit_number: event.target.value,
                  })}
                  value={editUnitForm.unit_number}
                />
              </Field>
              <Field label="Unit Type">
                <select
                  data-testid="structure-unit-edit-type"
                  onChange={(event) => setEditUnitForm({
                    ...editUnitForm,
                    unit_type: event.target.value,
                  })}
                  value={editUnitForm.unit_type}
                >
                  <option value="FLAT">Flat</option>
                  <option value="SHOP">Shop</option>
                  <option value="OFFICE">Office</option>
                  <option value="COMMERCIAL_UNIT">Commercial Unit</option>
                  <option value="GARAGE">Garage</option>
                  <option value="GODOWN">Godown</option>
                  <option value="OTHER">Other</option>
                </select>
              </Field>
              <Field label="Floor">
                <Input
                  data-testid="structure-unit-edit-floor"
                  onChange={(event) => setEditUnitForm({
                    ...editUnitForm,
                    floor: event.target.value,
                  })}
                  value={editUnitForm.floor}
                />
              </Field>
              <Field label="Carpet Area (Sq.Ft.)">
                <Input
                  data-testid="structure-unit-edit-carpet-area"
                  min="0"
                  onChange={(event) => setEditUnitForm({
                    ...editUnitForm,
                    carpet_area: event.target.value,
                  })}
                  step="0.01"
                  type="number"
                  value={editUnitForm.carpet_area}
                />
              </Field>
              <Field label="Status">
                <select
                  data-testid="structure-unit-edit-status"
                  onChange={(event) => setEditUnitForm({
                    ...editUnitForm,
                    status: event.target.value,
                  })}
                  value={editUnitForm.status}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="structure-unit-edit-save"
              onClick={saveUnitEdit}
            >
              Save Unit Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function StructureMetric({ label, value }) {
  return (
    <div className="border bg-card p-4" data-testid={`structure-metric-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-2xl font-bold">{value}</p>
    </div>
  );
}

function GovernancePanel({ canEdit, governance, onSaved }) {
  const [committee, setCommittee] = useState({ name: "", start_date: "", end_date: "" });
  const [signatory, setSignatory] = useState({ name: "", designation: "" });
  const post = async (path, payload, reset) => { try { await api.post(path, payload); toast.success("Governance record saved"); reset(); onSaved(); } catch (error) { toast.error(error.response?.data?.detail || "Could not save governance record"); } };
  if (!governance) return <Loading />;
  return <section className="mt-5 space-y-6" data-testid="society-governance-panel"><div className="grid gap-4 lg:grid-cols-2"><SetupCard icon={UsersRound} title="Committee terms"><SetupForm canEdit={canEdit} fields={[['Committee name', 'name'], ['Start date', 'start_date', 'date'], ['End date', 'end_date', 'date']]} onChange={(key, value) => setCommittee({ ...committee, [key]: value })} onSave={() => post('/society-setup/governance/committees', committee, () => setCommittee({ name: '', start_date: '', end_date: '' }))} values={committee} /></SetupCard><SetupCard icon={ShieldCheck} title="Authorized signatories"><SetupForm canEdit={canEdit} fields={[['Name', 'name'], ['Designation', 'designation']]} onChange={(key, value) => setSignatory({ ...signatory, [key]: value })} onSave={() => post('/society-setup/governance/signatories', signatory, () => setSignatory({ name: '', designation: '' }))} values={signatory} /></SetupCard></div><SimpleTable headers={['Committee', 'From', 'To', 'Status']} rows={(governance.committees || []).map((item) => [item.name, item.start_date, item.end_date || '—', item.status])} testId="society-committee-history" /><SimpleTable headers={['Signatory', 'Designation', 'Combination']} rows={(governance.signatories || []).map((item) => [item.name, item.designation, item.combination_group])} testId="society-signatories-table" /></section>;
}

function DocumentsPanel({ canEdit, docRef, documents, onSaved }) {
  const [meta, setMeta] = useState({ title: "", category: "Other", expiry_date: "" });
  const upload = async (event) => { const file = event.target.files?.[0]; if (!file) return; const form = new FormData(); form.append('file', file); Object.entries(meta).forEach(([key, value]) => form.append(key, value)); try { await api.post('/society-setup/documents/upload', form); toast.success('Document uploaded'); onSaved(); } catch (error) { toast.error(error.response?.data?.detail || 'Could not upload document'); } event.target.value = ''; };
  if (!documents) return <Loading />;
  return <section className="mt-5" data-testid="society-documents-panel"><div className="grid gap-3 border bg-card p-5 md:grid-cols-4"><Input data-testid="society-document-title" onChange={(event) => setMeta({ ...meta, title: event.target.value })} placeholder="Document title" value={meta.title} /><select data-testid="society-document-category" onChange={(event) => setMeta({ ...meta, category: event.target.value })} value={meta.category}>{CATEGORIES.map((category) => <option key={category}>{category}</option>)}</select><Input data-testid="society-document-expiry" onChange={(event) => setMeta({ ...meta, expiry_date: event.target.value })} type="date" value={meta.expiry_date} />{canEdit && <><input data-testid="society-document-input" hidden onChange={upload} ref={docRef} type="file" /><Button data-testid="society-document-upload" onClick={() => docRef.current?.click()}>Upload document</Button></>}</div>{documents.length === 0 ? <EmptyState icon={FileText} title="No society foundation documents yet" /> : <SimpleTable headers={['Title', 'Category', 'Expiry', 'Visibility']} rows={documents.map((item) => [item.title, item.category, item.expiry_date || '—', item.visibility])} testId="society-documents-table" />}</section>;
}

function FinancialPanel({ banks, canEdit, onSaved, profile, setProfile }) {
  const [bank, setBank] = useState({
    bank_name: "",
    branch: "",
    account_name: "",
    account_number: "",
    ifsc: "",
    upi_id: "",
  });

  const primaryBank = useMemo(
    () => (banks || []).find((item) => item.primary) || (banks || [])[0] || null,
    [banks],
  );

  useEffect(() => {
    setBank({
      bank_name: primaryBank?.bank_name || "",
      branch: primaryBank?.branch || "",
      account_name: primaryBank?.account_name || "",
      account_number: "",
      ifsc: primaryBank?.ifsc || "",
      upi_id: primaryBank?.upi_id || "",
    });
  }, [primaryBank]);

  const saveBank = async () => {
    try {
      const request = {
        ...bank,
        primary: true,
      };
      if (primaryBank) {
        await api.put(`/society-setup/bank-accounts/${primaryBank.bank_account_id}`, request);
      } else {
        await api.post("/society-setup/bank-accounts", request);
      }
      toast.success("Payment instructions saved for future bills");
      setBank((current) => ({ ...current, account_number: "" }));
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save payment instructions");
    }
  };

  const saveFoundation = async () => {
    try {
      await api.put("/society-setup/profile", {
        financial_settings: profile.financial_settings,
        default_billing_area_basis: profile.default_billing_area_basis || "CARPET_AREA",
        bill_addressee_mode: profile.bill_addressee_mode || "PRIMARY_OWNER_ONLY",
      });
      toast.success("Financial foundation saved");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save financial foundation");
    }
  };

  if (!banks) return <Loading />;

  return (
    <section className="mt-5 space-y-6" data-testid="society-financial-foundation">
      <div className="border bg-card p-5">
        <h2 className="font-display font-bold">Financial foundation</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          These preferences preserve current billing calculations. Carpet Area remains the active
          per Sq.Ft. billing input.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <Field label="Financial year starts">
            <Input
              data-testid="society-financial-year-start"
              disabled={!canEdit}
              onChange={(event) => setProfile({
                ...profile,
                financial_settings: {
                  ...profile.financial_settings,
                  financial_year_start: event.target.value,
                },
              })}
              value={profile.financial_settings?.financial_year_start || "04-01"}
            />
          </Field>
          <Field label="Billing cycle">
            <select
              data-testid="society-billing-cycle"
              disabled={!canEdit}
              onChange={(event) => setProfile({
                ...profile,
                financial_settings: {
                  ...profile.financial_settings,
                  billing_cycle: event.target.value,
                },
              })}
              value={profile.financial_settings?.billing_cycle || "MONTHLY"}
            >
              <option>MONTHLY</option>
              <option>QUARTERLY</option>
            </select>
          </Field>
          <Field label="Maintenance due day">
            <Input
              data-testid="society-maintenance-due-day"
              disabled={!canEdit}
              onChange={(event) => setProfile({
                ...profile,
                financial_settings: {
                  ...profile.financial_settings,
                  maintenance_due_day: event.target.value,
                },
              })}
              type="number"
              value={profile.financial_settings?.maintenance_due_day || ""}
            />
          </Field>
          <Field label="Default area basis">
            <select
              data-testid="society-default-billing-area-basis"
              disabled={!canEdit}
              onChange={(event) => setProfile({
                ...profile,
                default_billing_area_basis: event.target.value,
              })}
              value={profile.default_billing_area_basis || "CARPET_AREA"}
            >
              <option value="CARPET_AREA">Carpet Area</option>
              <option value="BUILT_UP_AREA">Built-up Area</option>
              <option value="SUPER_BUILT_UP_AREA">Super Built-up Area</option>
              <option value="CUSTOM_CHARGEABLE_AREA">Custom Chargeable Area</option>
            </select>
          </Field>
          <Field label="Financial document addressee">
            <select
              data-testid="society-bill-addressee-mode"
              disabled={!canEdit}
              onChange={(event) => setProfile({
                ...profile,
                bill_addressee_mode: event.target.value,
              })}
              value={profile.bill_addressee_mode || "PRIMARY_OWNER_ONLY"}
            >
              <option value="PRIMARY_OWNER_ONLY">Primary owner only</option>
              <option value="PRIMARY_AND_JOINT_OWNERS">Primary and joint owners</option>
              <option value="ALL_ELIGIBLE_ACTIVE_OWNERS">All eligible active owners</option>
            </select>
          </Field>
        </div>
        {canEdit && (
          <Button
            className="mt-4"
            data-testid="society-financial-settings-save"
            onClick={saveFoundation}
          >
            Save financial foundation
          </Button>
        )}
      </div>
      <section className="border bg-card p-5" data-testid="society-payment-instructions-panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-display font-bold">
              <Landmark className="h-4 w-4 text-primary" />
              Payment instructions on maintenance bills
            </div>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Use the account and UPI address members should use for maintenance payments.
            </p>
          </div>
          <span
            className="border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800"
            data-testid="society-payment-primary-status"
          >
            {primaryBank ? "Primary payment profile" : "Payment profile needed"}
          </span>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Field label="Bank name">
            <Input
              data-testid="society-payment-bank-name"
              disabled={!canEdit}
              onChange={(event) => setBank({ ...bank, bank_name: event.target.value })}
              value={bank.bank_name}
            />
          </Field>
          <Field label="Account holder name">
            <Input
              data-testid="society-payment-account-name"
              disabled={!canEdit}
              onChange={(event) => setBank({ ...bank, account_name: event.target.value })}
              value={bank.account_name}
            />
          </Field>
          <Field label="Account number">
            <Input
              data-testid="society-payment-account-number"
              disabled={!canEdit}
              onChange={(event) => setBank({ ...bank, account_number: event.target.value })}
              placeholder={primaryBank?.masked_account_number || "Enter account number"}
              value={bank.account_number}
            />
          </Field>
          <Field label="IFSC">
            <Input
              data-testid="society-payment-ifsc"
              disabled={!canEdit}
              onChange={(event) => setBank({ ...bank, ifsc: event.target.value.toUpperCase() })}
              value={bank.ifsc}
            />
          </Field>
          <Field label="Branch (optional)">
            <Input
              data-testid="society-payment-branch"
              disabled={!canEdit}
              onChange={(event) => setBank({ ...bank, branch: event.target.value })}
              value={bank.branch}
            />
          </Field>
          <Field label="UPI ID (optional)">
            <Input
              data-testid="society-payment-upi-id"
              disabled={!canEdit}
              onChange={(event) => setBank({ ...bank, upi_id: event.target.value })}
              placeholder="society@bank"
              value={bank.upi_id}
            />
          </Field>
        </div>
        {primaryBank && (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="society-payment-account-hint">
            Leave the account number blank to keep {primaryBank.masked_account_number} unchanged.
          </p>
        )}
        {canEdit && (
          <Button
            className="mt-5"
            data-testid="society-payment-instructions-save"
            onClick={saveBank}
          >
            Save payment instructions
          </Button>
        )}
      </section>
      <SimpleTable
        headers={["Bank", "Account", "IFSC", "UPI", "Primary"]}
        rows={banks.map((item) => [
          item.bank_name,
          item.masked_account_number,
          item.ifsc || "—",
          item.upi_id || "—",
          item.primary ? "Yes" : "No",
        ])}
        testId="society-bank-table"
      />
    </section>
  );
}

function SetupCard({ children, icon: Icon, title }) { return <section className="border bg-card p-5"><h2 className="flex items-center gap-2 font-display font-bold"><Icon className="h-4 w-4 text-primary" />{title}</h2><div className="mt-4">{children}</div></section>; }
function SetupForm({ canEdit, children, fields, onChange, onSave, values }) { return <div className="grid gap-3">{fields.map(([label, key, type = 'text']) => <Field key={key} label={label}><Input disabled={!canEdit} onChange={(event) => onChange(key, event.target.value)} type={type} value={values[key] || ''} /></Field>)}{children}{canEdit && <Button onClick={onSave} size="sm">Save</Button>}</div>; }
function Field({ children, label, wide }) { return <label className={wide ? 'grid gap-1 sm:col-span-2' : 'grid gap-1'}><Label>{label}</Label>{children}</label>; }
function SimpleTable({ headers, rows, testId }) { return <div className="mt-5 overflow-x-auto border bg-card" data-testid={testId}><table className="min-w-[560px] w-full text-sm"><thead className="bg-muted/60"><tr>{headers.map((header) => <th className="p-3 text-left" key={header}>{header}</th>)}</tr></thead><tbody>{rows.length === 0 ? <tr><td className="p-6 text-center text-muted-foreground" colSpan={headers.length}>No records yet.</td></tr> : rows.map((row, index) => <tr className="border-t" key={index}>{row.map((cell, cellIndex) => <td className="p-3" key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>; }